import { useState } from 'react';
import { Card, Nav, Badge, Button, Dropdown } from 'react-bootstrap';
import type { ProductData, CompetitorProduct } from '../models/types';
import InsertCompetitorProductModal from './InsertCompetitorProductModal';
import type { CompetitorClient } from '../../../../infrastructure/CompetitorClient';
import { useInsertCompetitorProduct } from '../../../../apps/hooks/competitor/useInsertCompetitorProduct';
import { PriceDisplay } from './PriceDisplay';

interface CompetitorSelectionSectionProps {
  readonly selectedProduct: ProductData;
  readonly activeCompetitor: string | null;
  readonly selectedProducts: Record<string, Record<string, string>>;
  readonly handleCompetitorProductSelect: (product?: CompetitorProduct) => void;
  readonly isProductSelected: (skinSeoulSku: string, competitor: string, competitorSku: string) => boolean;
  readonly setActiveCompetitor: (competitor: string | null) => void;
  readonly competitorClient: CompetitorClient;
  readonly onProductDataUpdate: (updatedProduct: ProductData) => void;
}

export default function CompetitorSelectionSection({
  selectedProduct,
  activeCompetitor,
  selectedProducts,
  handleCompetitorProductSelect,
  isProductSelected,
  setActiveCompetitor,
  competitorClient,
  onProductDataUpdate,
}: CompetitorSelectionSectionProps) {
  const [showInsertModal, setShowInsertModal] = useState(false);

  const { handleInsert } = useInsertCompetitorProduct(
    competitorClient,
    selectedProduct,
    onProductDataUpdate,
    setActiveCompetitor,
  );

  const competitorSearchUrls: Record<string, string> = {
    YesStyle: 'https://www.yesstyle.com/en/list.html?q=',
    Stylevana: 'https://www.stylevana.com/en_US/catalogsearch/result/?q=',
    StyleKorean: 'https://www.stylekorean.com/shop/search_result.php?keyword=',
    OliveYoung: 'https://global.oliveyoung.com/display/search?query=',
    Amazon: 'https://www.amazon.com/s?k=',
  };

  const handleCompetitorSearch = (competitor: string) => {
    if (!selectedProduct?.skinSeoulProduct?.name) {
      return;
    }
    const searchTerm = encodeURIComponent(selectedProduct.skinSeoulProduct.name);
    const baseUrl = competitorSearchUrls[competitor];
    window.open(`${baseUrl}${searchTerm}`, '_blank');
  };

  const getCompetitorProductsForCurrentCompetitor = (): CompetitorProduct[] => {
    if (!selectedProduct || !activeCompetitor) {
      return [];
    }

    const competitor = selectedProduct.machineMatchedProducts.find((comp) => comp.competitor === activeCompetitor);

    return competitor?.competitorProducts ?? [];
  };

  const handleCompetitorTabSelect = (competitorName: string | null) => {
    setActiveCompetitor(competitorName);
  };

  const competitorsWithProducts = selectedProduct.machineMatchedProducts.filter(
    (comp) => comp.competitorProducts.length > 0,
  );

  const skinSeoulProduct = selectedProduct.skinSeoulProduct;
  const competitorProducts = getCompetitorProductsForCurrentCompetitor();

  const getBadgeBackground = (possibility: number): string => {
    if (possibility > 0.8) {
      return 'success';
    }
    if (possibility > 0.6) {
      return 'warning';
    }
    return 'danger';
  };

  const getFirstImageUrl = (urls: any): string => {
    if (!urls) {
      return '';
    }

    // Handle array of URLs
    if (Array.isArray(urls)) {
      return urls.length > 0 ? urls[0].trim() : '';
    }

    // Handle string URLs
    if (typeof urls === 'string') {
      const firstUrl = urls.split(',')[0];
      return firstUrl ? firstUrl.trim() : '';
    }

    // For any other type, try to convert to string if possible
    try {
      const urlStr = String(urls);
      const firstUrl = urlStr.split(',')[0];
      return firstUrl ? firstUrl.trim() : '';
    } catch {
      return '';
    }
  };

  return (
    <div
      className="bg-white rounded-3 p-4 d-flex flex-column"
      style={{
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div
          className="text-uppercase"
          style={{ color: '#000000', fontSize: '16px', fontWeight: 600, letterSpacing: '1px' }}
        >
          Competitor Products
        </div>
        <div>
          <Dropdown className="d-inline-block me-2">
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              Search in Competitor
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.keys(competitorSearchUrls).map((competitor) => (
                <Dropdown.Item key={competitor} onClick={() => handleCompetitorSearch(competitor)}>
                  Search in {competitor}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="outline-primary" size="sm" onClick={() => setShowInsertModal(true)} className="me-2">
            Insert competitor product manually
          </Button>
          <Badge bg="primary" className="me-2">
            {competitorsWithProducts.length} competitors
          </Badge>
          <Badge bg="info">
            {selectedProduct.machineMatchedProducts.reduce((sum, comp) => sum + comp.competitorProducts.length, 0)}{' '}
            total matches
          </Badge>
        </div>
      </div>

      {competitorsWithProducts.length > 0 ? (
        <>
          <div className="mb-3">
            <Nav
              variant="tabs"
              activeKey={activeCompetitor ?? ''}
              onSelect={(key) => handleCompetitorTabSelect(key)}
              className="border-bottom-0"
            >
              {competitorsWithProducts.map((comp) => {
                const isSelected = selectedProducts?.[skinSeoulProduct.sku]?.[comp.competitor] !== undefined;

                return (
                  <Nav.Item key={comp.competitor}>
                    <Nav.Link eventKey={comp.competitor} className={isSelected ? 'text-success' : ''}>
                      {comp.competitor}
                      {isSelected && (
                        <Badge bg="success" className="ms-2">
                          ✓
                        </Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                );
              })}
            </Nav>
          </div>

          <div style={{ overflowY: 'auto', flexGrow: 1 }}>
            {activeCompetitor && competitorProducts.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {competitorProducts.map((product, index) => {
                  const isSelected = isProductSelected(skinSeoulProduct.sku, activeCompetitor, product.sku);

                  // Check if possibility exists and is a number
                  const possibility =
                    typeof (product as any).possibility === 'number' ? (product as any).possibility : null;

                  // Get price object if it exists
                  const price = (product as any).price || null;

                  return (
                    <Card
                      key={product.sku}
                      className={`border-0 ${isSelected ? 'border-success' : ''}`}
                      onClick={() => {
                        console.log('Card clicked - Product:', product);
                        console.log('Card clicked - isSelected:', isSelected);
                        console.log(
                          'Card clicked - handleCompetitorProductSelect:',
                          typeof handleCompetitorProductSelect,
                        );
                        handleCompetitorProductSelect(product);
                      }}
                      style={{
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        backgroundColor: isSelected ? '#f8fff8' : 'white',
                        transform: isSelected ? 'scale(1.01)' : 'scale(1)',
                      }}
                    >
                      <Card.Body>
                        <div className="d-flex gap-3">
                          <div className="position-relative" style={{ minWidth: '100px' }}>
                            {product.productImageUrls ? (
                              <img
                                src={getFirstImageUrl(product.productImageUrls)}
                                alt={product.normalizedName}
                                style={{
                                  width: '100px',
                                  height: '100px',
                                  objectFit: 'contain',
                                  borderRadius: '8px',
                                }}
                              />
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center bg-light"
                                style={{ width: '100px', height: '100px', borderRadius: '8px' }}
                              >
                                No image
                              </div>
                            )}
                            <div
                              className="position-absolute top-0 start-0 rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: '#3d1690',
                                color: 'white',
                                transform: 'translate(-30%, -30%)',
                              }}
                            >
                              {index + 1}
                            </div>
                            {isSelected && (
                              <div
                                className="position-absolute bottom-0 end-0 rounded-circle bg-success text-white d-flex align-items-center justify-content-center"
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  transform: 'translate(30%, 30%)',
                                }}
                              >
                                ✓
                              </div>
                            )}
                          </div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="mb-1">{product.normalizedName}</h6>
                              {possibility !== null && (
                                <Badge bg={getBadgeBackground(possibility)} style={{ fontSize: '0.75rem' }}>
                                  {Math.round(possibility * 100)}% match
                                </Badge>
                              )}
                            </div>
                            <div className="mb-1 small">SKU: {product.sku}</div>
                            <div className="mb-1 small">Brand: {product.brand}</div>
                            {price && (
                              <div className="text-primary fw-bold">
                                <PriceDisplay prices={price} />
                              </div>
                            )}
                            {product.productLink && (
                              <div className="mt-2">
                                <a
                                  href={product.productLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  View product in {activeCompetitor}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                {activeCompetitor ? 'No products found for this competitor' : 'Select a competitor to see products'}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center text-muted py-4">No competitor products found for this SkinSeoul product</div>
      )}

      <InsertCompetitorProductModal
        show={showInsertModal}
        onHide={() => setShowInsertModal(false)}
        onSubmit={handleInsert}
      />
    </div>
  );
}
